/**
 * Bulk Claude-friendly export of bug reports.
 *
 * Produces a zip file with structure:
 *
 *   bug-reports-2026-04-29-XXXX.zip
 *   ├── manifest.json                       — machine-readable index
 *   ├── README.md                           — overview (drop into Claude as context)
 *   └── reports/
 *       └── <reportId>/
 *           ├── report.md                   — human + Claude-readable
 *           ├── audit-log.json              — last N audit events at submit time
 *           ├── breadcrumbs.json            — UI breadcrumbs at submit time
 *           ├── comments.json               — full comment thread
 *           └── screenshots/
 *               ├── 1.png
 *               └── 2.png
 *
 * Modes:
 *   'all'   — every report
 *   'open'  — reports with status not in {confirmed, closed}
 *   'since' — reports with lastActivityAt > since (DM bookmark)
 *
 * Hand-rolled ZIP builder (no external dep). Uses STORED entries (no
 * compression) — beta volume is small enough that a few hundred KB to
 * MBs of unzipped bytes is fine, and avoiding the deflate dependency keeps
 * the bundle lean.
 *
 * On success returns `{ count, filename }`. The caller writes
 * lastBulkExportAt afterward (in ExportPanel) so the bookmark only
 * advances when the user actually downloaded the file.
 */

import { getAuthedBugReportClient } from './bugReportClient.js';

// --- ZIP construction -----------------------------------------------------

// CRC32 — lookup table approach. Standard polynomial 0xEDB88320.
let _crcTable = null;
const buildCrcTable = () => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    t[n] = c >>> 0;
  }
  return t;
};
const crc32 = (bytes) => {
  if (!_crcTable) _crcTable = buildCrcTable();
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) {
    c = _crcTable[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  }
  return (c ^ 0xFFFFFFFF) >>> 0;
};

const enc = (str) => new TextEncoder().encode(str);

// Concatenate a list of Uint8Arrays into one.
const concatBytes = (parts) => {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
};

const u16 = (n) => new Uint8Array([n & 0xFF, (n >>> 8) & 0xFF]);
const u32 = (n) => new Uint8Array([n & 0xFF, (n >>> 8) & 0xFF, (n >>> 16) & 0xFF, (n >>> 24) & 0xFF]);

/**
 * Build a ZIP from an array of `{ name: string, bytes: Uint8Array }`.
 * Uses STORED method (no compression). Each entry's local header carries
 * its CRC32 + size; the central directory at the end mirrors them.
 */
const buildZip = (entries) => {
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;

  for (const ent of entries) {
    const nameBytes = enc(ent.name);
    const crc = crc32(ent.bytes);
    const size = ent.bytes.length;

    // Local file header — signature 0x04034b50.
    const local = concatBytes([
      u32(0x04034b50),
      u16(20),     // version needed
      u16(0),      // general purpose
      u16(0),      // method = stored
      u16(0),      // mod time
      u16(0),      // mod date
      u32(crc),
      u32(size),   // compressed size = uncompressed
      u32(size),
      u16(nameBytes.length),
      u16(0),      // extra length
      nameBytes,
      ent.bytes,
    ]);
    localChunks.push(local);

    // Central directory record — signature 0x02014b50.
    const central = concatBytes([
      u32(0x02014b50),
      u16(20),     // version made by
      u16(20),     // version needed
      u16(0),      // gp flag
      u16(0),      // method
      u16(0),      // mod time
      u16(0),      // mod date
      u32(crc),
      u32(size),
      u32(size),
      u16(nameBytes.length),
      u16(0),      // extra
      u16(0),      // comment
      u16(0),      // disk no
      u16(0),      // int attrs
      u32(0),      // ext attrs
      u32(offset),
      nameBytes,
    ]);
    centralChunks.push(central);

    offset += local.length;
  }

  const central = concatBytes(centralChunks);
  const centralOffset = offset;
  const centralSize = central.length;

  // End-of-central-directory — signature 0x06054b50.
  const eocd = concatBytes([
    u32(0x06054b50),
    u16(0),       // disk
    u16(0),       // disk where central starts
    u16(entries.length),
    u16(entries.length),
    u32(centralSize),
    u32(centralOffset),
    u16(0),       // comment length
  ]);

  return concatBytes([...localChunks, central, eocd]);
};

// --- Markdown rendering ---------------------------------------------------

const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

const yamlEscape = (v) => {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return JSON.stringify(v);
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  if (/[:\n#'"`@]/.test(s) || s.includes('  ')) return JSON.stringify(s);
  return s;
};

/**
 * Render one report's markdown. Accepts an optional `imageRefs` array of
 * relative filenames (e.g. ["images/RPT-0001-01.png", ...]) to use as
 * inline screenshot links. When omitted, the screenshots section says
 * the bytes are not embedded in this format.
 */
const renderReportMarkdown = ({ report, screenshots, comments, referenceNumber, imageRefs }) => {
  const data = safeParse(report.data);
  const eff = exportFields(data);
  const tags = Array.isArray(eff.tags) ? eff.tags : [];
  const meta = data.appContext || {};
  const charSnap = data.characterSnapshot || null;
  const isDevEdited = !!data.devEdits;

  const fm = [
    '---',
    referenceNumber ? `ref: ${yamlEscape(referenceNumber)}` : null,
    `id: ${yamlEscape(report.reportId)}`,
    `status: ${yamlEscape(report.status)}`,
    `tags: ${yamlEscape(tags)}`,
    eff.importance != null ? `importance: ${eff.importance}` : null,
    eff.importanceRaw != null ? `importance_raw: ${eff.importanceRaw}` : null,
    eff.importanceScale != null ? `importance_scale: ${eff.importanceScale}` : null,
    isDevEdited ? `dev_edited: true` : null,
    `created: ${new Date(report.createdAt).toISOString()}`,
    `last_activity: ${new Date(report.lastActivityAt).toISOString()}`,
    `reporter_user_id: ${yamlEscape(report.reporterUserId || null)}`,
    `view: ${yamlEscape(meta.view)}`,
    `role: ${meta.isDM ? 'dm' : 'player'}`,
    `route: ${yamlEscape(meta.route)}`,
    `app_commit: ${yamlEscape(meta.commitSha)}`,
    meta.viewport ? `viewport: ${yamlEscape(meta.viewport)}` : null,
    charSnap ? `char_id: ${yamlEscape(charSnap.charId)}` : null,
    charSnap?.name ? `char_name: ${yamlEscape(charSnap.name)}` : null,
    `screenshot_count: ${(screenshots || []).length}`,
    `comment_count: ${(comments || []).length}`,
    '---',
  ].filter(Boolean).join('\n');

  const body = [];
  body.push(`# ${eff.title || '(untitled report)'}`);
  body.push('');
  if (isDevEdited) {
    // Surface that this report was edited so the agent reading the
    // export knows the title/description/tags/importance reflect the
    // dev's curation, not the reporter's raw submission.
    body.push(`> _Dev-edited. The reporter's original is preserved on the source record but not exported._`);
    body.push('');
  }
  if (eff.description) {
    body.push('## Description');
    body.push('');
    body.push(eff.description);
    body.push('');
  }

  if (charSnap) {
    body.push('## Character context');
    body.push('');
    body.push('```json');
    body.push(JSON.stringify(charSnap, null, 2));
    body.push('```');
    body.push('');
  }

  // If a recording was made, render a unified "Steps to reproduce" timeline
  // that interleaves clicks, audit events, and breadcrumbs sorted by ts.
  // This is the most Claude-friendly form: a single ordered narrative of
  // what the user did between record-start and record-stop.
  const isRecording = !!data.recordedSteps && (
    (Array.isArray(data.recordedInteractions) && data.recordedInteractions.length > 0)
    || (Array.isArray(data.lastAuditEvents) && data.lastAuditEvents.length > 0)
    || (Array.isArray(data.breadcrumbs) && data.breadcrumbs.length > 0)
  );

  if (isRecording) {
    const startedAt = data.recordedSteps?.startedAt;
    const endedAt = data.recordedSteps?.endedAt;
    const dur = (startedAt && endedAt) ? `${((endedAt - startedAt) / 1000).toFixed(1)}s` : '';
    body.push(`## Steps to reproduce (recorded${dur ? `, ${dur}` : ''})`);
    body.push('');

    const timeline = [];
    for (const it of (data.recordedInteractions || [])) {
      const el = it.element || {};
      const label =
        el.regionTag || el.ariaLabel || el.titleAttr ||
        (el.text ? `"${el.text}"` : null) ||
        el.testId || el.elementId || `<${el.tag || 'element'}>`;
      timeline.push({ ts: it.ts, label: `🖱️ click ${label} (${el.tag || 'el'})` });
    }
    for (const e of (data.lastAuditEvents || [])) {
      const summary = e.summary || `${e.category || '?'}.${e.action || '?'}${e.target ? ' → ' + e.target : ''}`;
      timeline.push({ ts: e.ts, label: `⚡ ${summary}` });
    }
    for (const b of (data.breadcrumbs || [])) {
      timeline.push({ ts: b.ts, label: `🧭 ${b.type}: ${b.value}` });
    }
    timeline.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    for (let i = 0; i < timeline.length; i++) {
      const t = timeline[i].ts ? new Date(timeline[i].ts).toISOString() : '(no ts)';
      body.push(`${i + 1}. \`${t}\` ${timeline[i].label}`);
    }
    body.push('');
  } else {
    if (Array.isArray(data.lastAuditEvents) && data.lastAuditEvents.length > 0) {
      body.push('## Last actions before report');
      body.push('');
      for (const e of data.lastAuditEvents) {
        const t = e.ts ? new Date(e.ts).toISOString() : '(no ts)';
        const summary = e.summary || `${e.category || '?'}.${e.action || '?'}${e.target ? ' → ' + e.target : ''}`;
        body.push(`- \`${t}\` ${summary}`);
      }
      body.push('');
    }

    if (Array.isArray(data.breadcrumbs) && data.breadcrumbs.length > 0) {
      body.push('## UI breadcrumbs');
      body.push('');
      for (const b of data.breadcrumbs) {
        const t = b.ts ? new Date(b.ts).toISOString() : '(no ts)';
        body.push(`- \`${t}\` ${b.type}: ${b.value}`);
      }
      body.push('');
    }
  }

  if (Array.isArray(data.elements) && data.elements.length > 0) {
    body.push('## Highlighted UI elements');
    body.push('');
    for (const el of data.elements) {
      body.push(`- ${el.regionTag || el.cssPath || '(unknown element)'}`);
    }
    body.push('');
  }

  if ((screenshots || []).length > 0) {
    body.push('## Screenshots');
    body.push('');
    if (imageRefs && imageRefs.length === screenshots.length) {
      // Zip-with-images export — inline references resolve to actual files
      // in the `images/` folder when the zip is extracted.
      for (let i = 0; i < imageRefs.length; i++) {
        body.push(`![Screenshot ${i + 1}](${imageRefs[i]})`);
      }
    } else {
      // Flat single-file export — bytes aren't included. Note as much.
      body.push(`*${screenshots.length} screenshot${screenshots.length === 1 ? '' : 's'} attached. Bytes are not embedded in this single-file export — re-export with "Include screenshots" checked to get the image files.*`);
    }
    body.push('');
  }

  if ((comments || []).length > 0) {
    body.push('## Comment thread');
    body.push('');
    for (const c of comments) {
      const who = c.authorRole === 'dm' ? 'DM' : 'reporter';
      const t = c.ts ? new Date(c.ts).toISOString() : '(no ts)';
      body.push(`### ${who} — ${t}`);
      body.push('');
      body.push(c.body || '(empty)');
      body.push('');
    }
  }

  if (Array.isArray(data.statusHistory) && data.statusHistory.length > 0) {
    body.push('## Status history');
    body.push('');
    for (const h of data.statusHistory) {
      const t = h.ts ? new Date(h.ts).toISOString() : '(no ts)';
      body.push(`- \`${t}\` ${h.from} → ${h.to}`);
    }
    body.push('');
  }

  return `${fm}\n\n${body.join('\n')}\n`;
};

const refNumberFor = (idx) => `RPT-${String(idx + 1).padStart(4, '0')}`;

// Per-screenshot relative path inside the zip. Stays the same regardless
// of where the user extracts the zip — they reference `images/RPT-...png`
// from the markdown / json.
const imagePath = (refNumber, idx, ext) =>
  `images/${refNumber}-${String(idx + 1).padStart(2, '0')}.${ext}`;

// Stable filenames for the export — `bug-reports-beta.{md,json,zip}`. The
// user extracts the zip into a `bug-reports/` folder of their choosing,
// so dating the file is unnecessary and creates churn for repeated
// exports overwriting (vs. piling up). The `manifest.json` inside the
// zip carries the timestamp.

const triggerDownload = (bytes, filename, mime) => {
  const blob = bytes instanceof Blob ? bytes : new Blob([bytes], { type: mime || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
};

// --- Main entry point -----------------------------------------------------

// "Open" filter excludes finished states. Covers both the new 3-state
// model (resolved) AND any legacy reports still tagged with 'closed' or
// 'confirmed' from before the simplification.
const FINISHED_STATUSES = new Set(['resolved', 'confirmed', 'closed']);

// --- Hydration ---------------------------------------------------------

// Collect + hydrate reports for the export. Returns the array of
// `{ report, screenshots, comments }` rows that match the filter.
async function collectAndHydrateReports({ mode, since }) {
  const client = await getAuthedBugReportClient();

  const candidates = [];
  let cursor = null;
  while (true) {
    let page;
    if (mode === 'since') {
      page = await client.listSinceLastExport({
        paginationOpts: { numItems: 50, cursor },
        since: since || 0,
      });
    } else {
      page = await client.list({
        paginationOpts: { numItems: 50, cursor },
      });
    }
    candidates.push(...page.page);
    if (page.isDone) break;
    cursor = page.continueCursor;
  }

  const filtered = mode === 'open'
    ? candidates.filter(r => !FINISHED_STATUSES.has(r.status))
    : candidates;

  if (filtered.length === 0) return [];

  const reportIds = filtered.map(r => r.reportId);
  const hydrated = [];
  for (let i = 0; i < reportIds.length; i += 25) {
    const batch = reportIds.slice(i, i + 25);
    const rows = await client.getBulkExport(batch);
    hydrated.push(...rows);
  }
  // 2026-05-01: drop reports the dev marked excludeFromExport (jokes,
  // duplicates, noise). They stay in the system but never reach the
  // exported bundle. Filter happens here, after hydration but before
  // any rendering, so every downstream consumer sees the filtered set.
  return hydrated.filter(h => {
    try {
      const data = JSON.parse(h.report.data || '{}');
      return !data.excludeFromExport;
    } catch { return true; }
  });
}

// 2026-05-01: dev-edit overlay applied at export time. The reporter's
// original `title` / `description` / `tags` / `importance` stay on the
// data blob; the dev's overrides land on `data.devEdits`. The export
// always uses the dev's view (overrides win). Mirrors the
// `effectiveData` helper in reportData.js — duplicated here to keep
// claudeExport.js self-contained for the script-side runner.
const exportFields = (data) => {
  const o = data?.devEdits || {};
  return {
    title:           ('title'           in o) ? o.title           : data.title,
    description:     ('description'     in o) ? o.description     : data.description,
    tags:            ('tags'            in o) ? o.tags            : data.tags,
    importance:      ('importance'      in o) ? o.importance      : data.importance,
    importanceRaw:   ('importanceRaw'   in o) ? o.importanceRaw   : data.importanceRaw,
    importanceScale: ('importanceScale' in o) ? o.importanceScale : data.importanceScale,
  };
};

// --- Image fetching (used by zip exports) ------------------------------

/**
 * For each hydrated report, fetch its screenshot bytes from the signed URLs
 * and assign relative image paths under `images/RPT-NNNN-NN.ext`. Returns
 * the augmented hydrated list with `imageRefs` and `imageEntries`. Failed
 * fetches are skipped silently (with a warning).
 */
async function fetchScreenshotsForExport(hydrated) {
  const out = [];
  for (let i = 0; i < hydrated.length; i++) {
    const h = hydrated[i];
    const refNumber = refNumberFor(i);
    const imageRefs = [];
    const imageEntries = []; // { name, bytes } for the zip
    for (let j = 0; j < (h.screenshots || []).length; j++) {
      const sc = h.screenshots[j];
      if (!sc.url) continue;
      try {
        const res = await fetch(sc.url);
        if (!res.ok) {
          console.warn(`Skipping screenshot ${refNumber}-${j + 1} — fetch failed (${res.status})`);
          continue;
        }
        const buf = await res.arrayBuffer();
        const ext = (sc.mime && sc.mime.startsWith('image/'))
          ? sc.mime.slice(6).split('+')[0].replace('jpeg', 'jpg')
          : 'png';
        const name = imagePath(refNumber, j, ext);
        imageRefs.push(name);
        imageEntries.push({ name, bytes: new Uint8Array(buf) });
      } catch (e) {
        console.warn(`Skipping screenshot ${refNumber}-${j + 1}:`, e);
      }
    }
    out.push({ ...h, refNumber, imageRefs, imageEntries });
  }
  return out;
}

// --- Markdown body (single text composition, with or without images) ----

const composeMarkdownText = (hydratedWithRefs, { mode, since, withImages }) => {
  const lines = [];
  lines.push('# Bug Reports Export');
  lines.push('');
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push(`Filter: ${mode}${mode === 'since' && since ? ` (since ${new Date(since).toISOString()})` : ''}`);
  lines.push(`Count: ${hydratedWithRefs.length} report${hydratedWithRefs.length === 1 ? '' : 's'}`);
  lines.push('');
  if (withImages) {
    lines.push('> Bundle layout: this `bug-reports-beta.md` plus an `images/` sibling folder. Each report references its screenshots as `images/RPT-NNNN-NN.png` — relative paths that resolve when the zip is extracted.');
  } else {
    lines.push('> Single-file export. Screenshot bytes are not embedded — re-export with "Include screenshots" checked to get the actual files.');
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  for (const h of hydratedWithRefs) {
    lines.push(renderReportMarkdown({
      ...h,
      referenceNumber: h.refNumber,
      imageRefs: withImages ? h.imageRefs : null,
    }));
    lines.push('');
    lines.push('---');
    lines.push('');
  }
  return lines.join('\n');
};

const composeJsonPayload = (hydratedWithRefs, { mode, since, withImages }) => {
  return {
    generatedAt: new Date().toISOString(),
    mode,
    since: mode === 'since' ? (since || 0) : null,
    count: hydratedWithRefs.length,
    layout: withImages
      ? 'Zip bundle: bug-reports-beta.json at root + images/ folder. Each report.screenshots[i].filename is a path relative to the zip root.'
      : 'Single file. Screenshot bytes are not embedded — re-export with "Include screenshots" checked to get the actual files.',
    reports: hydratedWithRefs.map((h) => {
      const data = safeParse(h.report.data);
      const eff = exportFields(data);
      return {
        ref: h.refNumber,
        id: h.report.reportId,
        status: h.report.status,
        createdAt: h.report.createdAt,
        lastActivityAt: h.report.lastActivityAt,
        reporterUserId: h.report.reporterUserId || null,
        devEdited: !!data.devEdits,
        title: eff.title || null,
        description: eff.description || null,
        tags: eff.tags || [],
        importance: eff.importance ?? null,
        importanceRaw: eff.importanceRaw ?? null,
        importanceScale: eff.importanceScale ?? null,
        appContext: data.appContext || null,
        characterSnapshot: data.characterSnapshot || null,
        elements: data.elements || [],
        recordedSteps: data.recordedSteps || null,
        recordedInteractions: data.recordedInteractions || null,
        lastAuditEvents: data.lastAuditEvents || [],
        breadcrumbs: data.breadcrumbs || [],
        statusHistory: data.statusHistory || [],
        screenshots: (h.screenshots || []).map((s, i) => ({
          order: s.order,
          uploadedAt: s.uploadedAt,
          filename: withImages ? (h.imageRefs[i] || null) : null,
        })),
        comments: (h.comments || []).map(c => ({
          ts: c.ts,
          authorRole: c.authorRole,
          body: c.body,
        })),
      };
    }),
  };
};

// --- Manifest (machine-readable index for the zip) ----------------------

const composeManifest = (hydratedWithRefs, { mode, since, withImages }) => ({
  generatedAt: new Date().toISOString(),
  mode,
  since: mode === 'since' ? (since || 0) : null,
  count: hydratedWithRefs.length,
  withImages,
  reports: hydratedWithRefs.map(h => {
    const data = safeParse(h.report.data);
    const eff = exportFields(data);
    return {
      ref: h.refNumber,
      id: h.report.reportId,
      status: h.report.status,
      devEdited: !!data.devEdits,
      tags: eff.tags || [],
      importance: eff.importance ?? null,
      title: eff.title || null,
      createdAt: h.report.createdAt,
      lastActivityAt: h.report.lastActivityAt,
      reporterUserId: h.report.reporterUserId || null,
      screenshotCount: (h.imageEntries || []).length,
      screenshotPaths: h.imageRefs || [],
      commentCount: (h.comments || []).length,
    };
  }),
});

// --- Format branches ----------------------------------------------------

async function exportFlatMarkdown({ mode, since, hydrated }) {
  const hydratedWithRefs = hydrated.map((h, i) => ({ ...h, refNumber: refNumberFor(i), imageRefs: [], imageEntries: [] }));
  const text = composeMarkdownText(hydratedWithRefs, { mode, since, withImages: false });
  const filename = 'bug-reports-beta.md';
  triggerDownload(text, filename, 'text/markdown;charset=utf-8');
  return { count: hydrated.length, filename };
}

async function exportFlatJson({ mode, since, hydrated }) {
  const hydratedWithRefs = hydrated.map((h, i) => ({ ...h, refNumber: refNumberFor(i), imageRefs: [], imageEntries: [] }));
  const payload = composeJsonPayload(hydratedWithRefs, { mode, since, withImages: false });
  const text = JSON.stringify(payload, null, 2);
  const filename = 'bug-reports-beta.json';
  triggerDownload(text, filename, 'application/json;charset=utf-8');
  return { count: hydrated.length, filename };
}

async function exportZipBundle({ mode, since, hydrated, format }) {
  const hydratedWithRefs = await fetchScreenshotsForExport(hydrated);

  const zipEntries = [];
  // Top-level manifest — always JSON, gives programmatic access to the
  // bundle's structure.
  zipEntries.push({
    name: 'manifest.json',
    bytes: enc(JSON.stringify(composeManifest(hydratedWithRefs, { mode, since, withImages: true }), null, 2)),
  });

  // Top-level human/Claude-readable file (md or json depending on format).
  if (format === 'json') {
    const payload = composeJsonPayload(hydratedWithRefs, { mode, since, withImages: true });
    zipEntries.push({
      name: 'bug-reports-beta.json',
      bytes: enc(JSON.stringify(payload, null, 2)),
    });
  } else {
    const md = composeMarkdownText(hydratedWithRefs, { mode, since, withImages: true });
    zipEntries.push({
      name: 'bug-reports-beta.md',
      bytes: enc(md),
    });
  }

  // images/ folder — one image per screenshot, named RPT-NNNN-MM.ext.
  for (const h of hydratedWithRefs) {
    for (const ent of h.imageEntries) {
      zipEntries.push(ent);
    }
  }

  const zipBytes = buildZip(zipEntries);
  const filename = 'bug-reports-beta.zip';
  triggerDownload(zipBytes, filename, 'application/zip');
  return { count: hydrated.length, filename };
}

// --- Public dispatcher --------------------------------------------------

/**
 * Export bug reports.
 *
 * @param {object} opts
 * @param {'all'|'open'|'since'} opts.mode
 * @param {number} [opts.since]
 * @param {'md'|'json'} [opts.format='md']
 *   The text format inside the export. `md` is human/Claude-readable
 *   markdown; `json` is structured JSON for programmatic use.
 * @param {boolean} [opts.includeScreenshots=false]
 *   When true, wraps the text file in a zip alongside an `images/` folder
 *   so screenshot bytes travel with the report. When false, downloads the
 *   text file directly with no images.
 *
 * Output filenames:
 *   md  + flat   → `bug-reports-beta.md`
 *   json + flat  → `bug-reports-beta.json`
 *   md  + zip    → `bug-reports-beta.zip` containing `bug-reports-beta.md`
 *                   + `manifest.json` + `images/RPT-NNNN-MM.png`
 *   json + zip   → `bug-reports-beta.zip` containing `bug-reports-beta.json`
 *                   + `manifest.json` + `images/RPT-NNNN-MM.png`
 *
 * @returns {Promise<{count: number, filename: string|null}>}
 */
export async function exportBugReports({ mode, since, format = 'md', includeScreenshots = false }) {
  const hydrated = await collectAndHydrateReports({ mode, since });
  if (hydrated.length === 0) return { count: 0, filename: null };

  if (includeScreenshots) {
    return exportZipBundle({ mode, since, hydrated, format });
  }
  if (format === 'json') return exportFlatJson({ mode, since, hydrated });
  return exportFlatMarkdown({ mode, since, hydrated });
}
